import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useTranslator } from 'tradera/lang/translate';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import AppleLoginButton from './apple-login-button';
import KlarnaLoginButton from './klarna-login-button';
import ErrorIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';
import FacebookLoginButton from './facebook-login-button';
import GoogleLoginButton from './google-login-button';
import { Divider } from 'tradera/components/divider/divider';
import {
    isDenmarkCountryCode,
    isNetherlandsCountryCode,
    isSwedenCountryCodeOrUndefined
} from 'tradera/localization/countrycode';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { useSelector } from 'react-redux';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';

const SocialLogins = ({
    appleClientId,
    facebookAppId,
    className,
    googleAppId,
    isModal,
    klarnaAppId,
    klarnaEnvironment
}) => {
    const isAnySocialLoginEnabled =
        appleClientId || facebookAppId || googleAppId || klarnaAppId;
    const [error, setError] = useState(null);
    const { t } = useTranslator();
    const isMobile = useIsMobile();
    const countryCode = useSelector(selectShippingRegionCountryCodeIso2);
    const isKlarnaForeignEnabled = useIsFeatureEnabled('klarna-login-foreign');

    if (!isAnySocialLoginEnabled) {
        return null;
    }

    const showKlarnaLogin =
        isSwedenCountryCodeOrUndefined(countryCode) ||
        (isKlarnaForeignEnabled &&
            (isDenmarkCountryCode(countryCode) ||
                isNetherlandsCountryCode(countryCode)));

    return (
        <div className={className}>
            <div className="d-flex d-md-block justify-content-around py-1">
                {googleAppId && (
                    <GoogleLoginButton
                        googleAppId={googleAppId}
                        className="my-1"
                        compact={isMobile}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                    />
                )}
                {klarnaAppId && showKlarnaLogin && (
                    <KlarnaLoginButton
                        klarnaAppId={klarnaAppId}
                        klarnaEnvironment={klarnaEnvironment}
                        klarnaMarket={countryCode.toUpperCase()}
                        className="my-1"
                        compact={isMobile}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                        tabIndex={0}
                    />
                )}
                {facebookAppId && (
                    <FacebookLoginButton
                        className="my-1"
                        compact={isMobile}
                        facebookAppId={facebookAppId}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                        tabIndex={0}
                    />
                )}
                {appleClientId && (
                    <AppleLoginButton
                        appleClientId={appleClientId}
                        className="my-1"
                        compact={isMobile}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                        tabIndex={0}
                    />
                )}
            </div>
            {error && (
                <div className="alert alert-danger text-left my-1">
                    <p className="mb-0">
                        <ErrorIcon className="icon align-text-bottom" />
                        &nbsp;{t('login_error')}
                    </p>
                </div>
            )}

            <Divider role="presentation">
                <strong className="size-oslo text-styled">
                    {t('login_orEnter')}
                </strong>
            </Divider>
        </div>
    );
};

SocialLogins.propTypes = {
    appleClientId: PropTypes.string,
    facebookAppId: PropTypes.string,
    googleAppId: PropTypes.string,
    klarnaAppId: PropTypes.string,
    className: PropTypes.string,
    isModal: PropTypes.bool
};

SocialLogins.defaultProps = {
    className: ''
};

export default SocialLogins;
